import React, { useState, useEffect } from "react";
import apiClient from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Tag } from "lucide-react";

const categories = ["STANDARD", "ETUDIANT", "SENIOR", "FAMILIAL", "PRIO_PACK", "SINGLE_ENTRY"];
const catLabels = { STANDARD: "Standard", ETUDIANT: "Étudiant", SENIOR: "Senior", FAMILIAL: "Familial", PRIO_PACK: "Libre accès Prio", SINGLE_ENTRY: "Entrée unique" };
const catColors = { STANDARD: "bg-blue-100 text-blue-700", ETUDIANT: "bg-purple-100 text-purple-700", SENIOR: "bg-teal-100 text-teal-700", FAMILIAL: "bg-pink-100 text-pink-700", PRIO_PACK: "bg-orange-100 text-orange-700", SINGLE_ENTRY: "bg-gray-100 text-gray-700" };

const CatalogPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ category: "STANDARD", name: "", price: "", duration_desc: "", payment_type: "ANY" });
  const { isAdmin } = useAuth();

  const fetch = async () => {
    const r = await apiClient.get("/catalog");
    setItems(r.data);
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);

  const openEdit = (item) => {
    setEditItem(item);
    setForm({ category: item.category, name: item.name, price: String(item.price), duration_desc: item.duration_desc || "", payment_type: item.payment_type || "ANY" });
  };

  const openAdd = () => {
    setEditItem(null);
    setForm({ category: "STANDARD", name: "", price: "", duration_desc: "", payment_type: "ANY" });
    setShowAdd(true);
  };

  const handleSave = async () => {
    const payload = { ...form, price: Number(form.price) };
    if (editItem) {
      await apiClient.put(`/catalog/${editItem.id}`, payload);
    } else {
      await apiClient.post("/catalog", payload);
    }
    setShowAdd(false);
    setEditItem(null);
    await fetch();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce plan ?")) return;
    await apiClient.delete(`/catalog/${id}`);
    await fetch();
  };

  const grouped = categories.reduce((acc, cat) => {
    acc[cat] = items.filter((i) => i.category === cat);
    return acc;
  }, {});

  return (
    <div className="crm-page">
      <div className="crm-page-header">
        <div>
          <h1 className="crm-page-title">Catalogue abonnements</h1>
          <p className="crm-page-subtitle">{items.length} plans configurés</p>
        </div>
        {isAdmin && (
          <Button onClick={openAdd} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]">
            <Plus size={16} className="mr-1" /> Nouveau plan
          </Button>
        )}
      </div>

      {loading ? <p className="py-8 text-center text-[#577083]">Chargement...</p> : (
        <div className="flex flex-col gap-6">
          {categories.map((cat) => grouped[cat]?.length > 0 && (
            <div key={cat} className="crm-card">
              <div className="flex items-center gap-2 mb-4">
                <Tag size={16} className="text-[#577083]" />
                <h3 className="crm-card-title mb-0">{catLabels[cat]}</h3>
                <span className={`crm-badge ${catColors[cat]}`}>{grouped[cat].length}</span>
              </div>
              <div className="crm-table-wrap" style={{boxShadow: 'none'}}>
                <table className="crm-table">
                  <thead>
                    <tr><th>Nom</th><th>Prix</th><th>Durée</th><th>Paiement</th>{isAdmin && <th>Actions</th>}</tr>
                  </thead>
                  <tbody>
                    {grouped[cat].map((item) => (
                      <tr key={item.id}>
                        <td className="font-medium">{item.name}</td>
                        <td className="font-semibold">{item.price}€</td>
                        <td className="text-[#577083]">{item.duration_desc}</td>
                        <td><span className="crm-badge bg-[#edf0f4] text-[#32536a]">{item.payment_type}</span></td>
                        {isAdmin && (
                          <td>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => { openEdit(item); setShowAdd(true); }}><Pencil size={14} /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)}><Trash2 size={14} className="text-red-400" /></Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAdd || !!editItem} onOpenChange={(v) => { if (!v) { setShowAdd(false); setEditItem(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editItem ? "Modifier le plan" : "Nouveau plan"}</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c} value={c}>{catLabels[c]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Nom du plan" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
            <Input type="number" placeholder="Prix €" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} />
            <Input placeholder="Description durée" value={form.duration_desc} onChange={(e) => setForm({...form, duration_desc: e.target.value})} />
            <Select value={form.payment_type} onValueChange={(v) => setForm({...form, payment_type: v})}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ANY">Tous types</SelectItem>
                <SelectItem value="DOMICILIATION">Domiciliation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAdd(false); setEditItem(null); }}>Annuler</Button>
            <Button onClick={handleSave} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CatalogPage;
